import { useRef, useState, type FormEvent } from "react";
import { Recolher, useTremor } from "@vertex/ui/movimento";
import { buttonClass } from "@vertex/ui/primitives/button-style";
import { Spinner } from "@vertex/ui/primitives/spinner";
import { ApiError } from "../lib/api";
import { pdvApi } from "../lib/pdv";

/**
 * Entrada do terminal. Duas credenciais, um campo só: o servidor decide pelo
 * "@" se é e-mail do usuário alocado ou a credencial do PDV (`pdv-xxxx`).
 * Pedir ao operador que escolha o tipo antes de digitar seria uma pergunta que
 * o sistema já sabe responder.
 */
export function LoginScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [loginUsername, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* VOS Movimento: senha recusada é o "não" do sistema, como no web (founder
     26/09): o campo da senha TREME (`useTremor`, 4px, três idas; só porque a
     pessoa tentou), o aviso abre pela altura (`Recolher`) e o foco volta pra
     senha, que já veio limpa pra digitar de novo. */
  const [campoDaSenha, tremer] = useTremor<HTMLDivElement>();
  const senhaRef = useRef<HTMLInputElement>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await pdvApi.login(loginUsername.trim(), password);
      onAuthenticated();
    } catch (err) {
      /* Mensagem do servidor quando ela é específica ("não está alocado a
         nenhum PDV" diz ao operador o que fazer); genérica caso contrário. */
      setError(
        err instanceof ApiError ? err.message : "Não foi possível entrar. Tente novamente.",
      );
      setPassword("");
      tremer();
      senhaRef.current?.focus({ preventScroll: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-[380px] rounded-xl p-8"
        style={{ background: "var(--panel)", border: "1px solid var(--line)", boxShadow: "var(--shadow-lg)" }}
      >
        <h1
          className="mb-1 font-semibold tracking-tight"
          style={{ fontSize: "var(--fs-h3)" }}
        >
          Terminal de Venda
        </h1>
        <p className="mb-6" style={{ fontSize: "var(--fs-meta)", color: "var(--ink-3)" }}>
          Entre com seu e-mail ou a credencial do caixa.
        </p>

        <div className="pdv-field mb-4">
          <label htmlFor="pdv-user">Usuário</label>
          <input
            id="pdv-user"
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={loginUsername}
            onChange={(e) => setUser(e.target.value)}
            placeholder="voce@empresa.com ou pdv-loja01"
          />
        </div>

        <div ref={campoDaSenha} className="pdv-field mb-6">
          <label htmlFor="pdv-pass">Senha</label>
          <input
            ref={senhaRef}
            id="pdv-pass"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {/* O vão de baixo mora no miolo (padding), não na margem do aviso: a
            margem vazaria da conta de altura e daria um tranco no fim. */}
        <Recolher aberto={Boolean(error)}>
          <div className="pb-4">
            <p
              role="alert"
              className="rounded-lg px-3 py-2"
              style={{
                fontSize: "var(--fs-meta)",
                color: "var(--bad)",
                background: "var(--bad-soft)",
              }}
            >
              {error}
            </p>
          </div>
        </Recolher>

        <button type="submit" disabled={busy} className={`${buttonClass("primary", "lg")} w-full`}>
          {busy ? <Spinner /> : "Entrar"}
        </button>
      </form>
    </div>
  );
}
